import { Button } from "@/components/ui/Button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/Dialog";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { cn } from "@/utils/cn";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Download, ExternalLink, FileText, Info, Link2, Loader2, MessageSquare, MessageSquarePlus, Plus, RefreshCw, ScanSearch } from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  addFastColumn,
  clearFastCells,
  deleteFastColumn,
  deleteFastRows,
  FastQuotaError,
  reorderFastColumns,
  downloadFastCsv,
  type FastMethods,
  fastDatasetsKey,
  fastQuotaKey,
  getFastDataset,
  getFastMethods,
  getFastProgress,
  getSharedFastDataset,
  planFastDataset,
  rankFastColumn,
  type ReviewedColumn,
  type ReviewedSources,
  shareFastDataset,
  sharedFastUrl,
  streamFastAutoBuild,
  streamFastBroaden,
  streamFastMonitorRun,
  streamFastMore,
  streamFastReviewedBuild,
  unshareFastDataset,
  updateFastColumn,
  useFastDatasets,
  useFastQuota,
  writeFastMethods,
} from "../api";
import { ApiError } from "@/api/fetch";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { claimAnonDataset, clearAnonClaim, loginHref, readAnonClaim, streamAnonBuild } from "../anonymous";
import { BuildOptions, DEFAULT_ROWS, allowedRows, useAutoApprove } from "../components/BuildOptions";
import { QUOTA_ERROR, QuotaLine, QuotaReached, RowLimitNote } from "../components/PlanLimits";
import { ColumnPanel, type ColumnDraft } from "../components/ColumnPanel";
import { ColumnReview } from "../components/ColumnReview";
import { FAST_ROUTE } from "../components/FastDatasetList";
import { plannedSources } from "../components/SourceReview";
import { citationsText, designName, originalLink, paperName } from "../cite";
import { EvidencePanel, cellOrigin, cellText } from "../components/EvidencePanel";
import { BuildStages, CheckingLine, FigureScanning, PulseBlock, SkeletonRows } from "../components/BuildProgress";
import { ScrollFade, useVisibleWidth } from "../components/ScrollFade";
import { FastDatasetChat } from "../components/FastDatasetChat";
import { DatasetsFaq } from "../components/DatasetsFaq";
import { type ChatCell, chatCellKey, withCells } from "../chatCells";
import { MethodsDialog } from "../components/MethodsDialog";
import { NormalizeDialog } from "../components/NormalizeDialog";
import { AgentPromptBox } from "@/features/data-curation/components/AgentPromptBox";
import { type FieldMenu, SheetMenu } from "../components/SheetMenu";
import { type Filters, PAPER_FIELDS, type Sort, filterValues, kindOf, view } from "../sheet";
import type { FastCell, FastColumn, FastDataset, FastDatasetSummary, FastEvent, FastFigure, FastMonitor, FastPlan, FastRow, FastSource, FastStats, FastStatus, SearchProgress } from "../types";
import { FadeText } from "../components/Fade";
import { TextSizeToggle, useTextSize } from "../components/TextSize";

/** At most six. A sample fills the request box: building one would spend one of the plan's datasets. */
const SAMPLES = [
  "Build a dataset of weight loss with GLP-1 drugs like semaglutide and tirzepatide, by drug and dose",
  "Build a dataset of CAR-T response rates in relapsed multiple myeloma, by product",
  "Build a dataset of psilocybin trials for depression, with their effect sizes",
  "Build a dataset of ARIA rates with anti-amyloid antibodies for Alzheimer's disease, by drug",
  "Build a dataset of patents on KRAS G12D inhibitors",
  "Build a dataset of cardiovascular outcome trials of SGLT2 inhibitors, with hazard ratios",
];
/** Build timer; ticks every frame by writing the text itself so the table does not re-render. */
const Elapsed = ({ startedAt, endedAt, fallbackMs }: { startedAt: number | null; endedAt: number | null; fallbackMs: number }) => {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const show = (ms: number) => {
      if (ref.current) ref.current.textContent = ms > 0 ? `${(ms / 1000).toFixed(1)} s` : "";
    };
    if (!startedAt) return show(fallbackMs);
    if (endedAt) return show(endedAt - startedAt);
    let frame = 0;
    const tick = () => {
      show(Date.now() - startedAt);
      frame = window.requestAnimationFrame(tick);
    };
    tick();
    return () => window.cancelAnimationFrame(frame);
  }, [startedAt, endedAt, fallbackMs]);
  return <span ref={ref} className="tabular-nums" />;
};

/** Header lengths of the columns drawn while the real ones are planned. */
const PLACEHOLDER_HEADERS = [9, 13, 7, 11, 15, 8];
const FLASH_MS = 1600;
/** How often a dataset in review looks for an approval made elsewhere (the CLI, another tab). */
const REVIEW_POLL_MS = 3000;
/** How often a page reloads a build it does not hold the stream of (another tab, the CLI, a cut stream). */
const RUNNING_POLL_MS = 3000;
const EMPTY_RESULT = "No studies matched. Try rewording the question or making it broader.";
/** The builder's failure texts point at More rows, which needs a build to continue. */
const withoutMoreRows = (error: string) => error.replace(/[;.]?\s*use More rows to continue\.?/i, ".");

type Selection = { rowKey: string; columnKey: string | null } | null;

const methodsOf = (d: FastDataset): FastMethods => ({ methods: d.methods ?? null, methods_status: d.methods_status ?? null });

const monitorOf = (d: FastMonitor): FastMonitor => ({
  monitor_enabled: d.monitor_enabled,
  monitor_next_at: d.monitor_next_at,
  monitor_last_at: d.monitor_last_at,
  monitor_last_rows: d.monitor_last_rows,
  monitor_last_error: d.monitor_last_error,
});

const agentPrompt = (id: string, question: string) => {
  const name = question.length > 120 ? `${question.slice(0, 117)}...` : question;
  return `I'm handing you a Paperclip dataset: "${name}" (id ${id}). Read it first: paperclip dataset show ${id} lists its columns, how many rows have each and the category counts; paperclip dataset read ${id} --out rows.csv saves every row (--format json adds the quote and source behind each value). If the paperclip command is missing, install it with curl -fsSL ${window.location.origin}/install.sh | bash, then paperclip login; run paperclip update if that command or those options are missing. paperclip dataset --help lists the commands that edit it. Then tell me briefly what's in it and help me with what I ask next.`;
};
const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

/** Columns that define the rows: shown, not edited or deleted. */
const FIXED_COLUMNS = new Set(["drug", "dose"]);
/** The columns each paper's rows are split on; figure rows are split on figures. */
const fixedColumns = (plan: FastPlan | null) => (plan?.planner_state?.figure_rows ? new Set<string>() : FIXED_COLUMNS);

/** A column's span question as a description: its placeholders read as words. */
const describe = (column: FastColumn) =>
  column.question.replace(/\{iv\}/g, "the treatment").replace(/\{arm\}/g, "each group");

/** The page is drawn at this scale so more of the table fits; its height makes up for the zoom. */
const PAGE_ZOOM = 0.84;

/** The Evidence API's order: its paper score (relevance x study-design weight), highest first. */
/** Pinned to the left of the scrolling table and as wide as the part of it in view. */
const VisibleWidth = ({ children }: { children: React.ReactNode }) => {
  const width = useVisibleWidth();
  return (
    <div className="sticky left-0" style={{ width: width || undefined }}>
      {children}
    </div>
  );
};

const figureUrl = (path: string) => `/api${path}`;

/** An image column's cell: the paper's figure, opening full size in a popup; its caption on hover. An image
 * that does not load counts as no value. */
const FigureCell = ({ figure, docId }: { figure?: FastFigure; docId: string }) => {
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  if (!figure || failed) return <span className="text-zinc-300">—</span>;
  const other = figure.document_id && !figure.document_id.startsWith("pmid:") && figure.document_id !== docId
    ? figure.document_id
    : null;
  const title = [figure.label, figure.caption].filter(Boolean).join(": ") + (other ? ` (from ${other})` : "");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title={title}
        className="block w-[120px] cursor-zoom-in text-left"
      >
        <img
          src={figureUrl(figure.path)}
          alt={title || "Figure"}
          loading="lazy"
          onError={() => setFailed(true)}
          className="max-h-24 w-full rounded-[2px] border border-zinc-200 bg-white object-contain"
        />
        {other && <FadeText className="font-ui mt-0.5 text-[10px] text-zinc-400">from {other}</FadeText>}
      </button>
      <DialogContent className="max-w-5xl p-4" onClick={(e) => e.stopPropagation()}>
        <DialogTitle className="pr-6 text-base">{figure.label || "Figure"}{other ? ` (from ${other})` : ""}</DialogTitle>
        <img src={figureUrl(figure.path)} alt={title || "Figure"} className="max-h-[75vh] w-full object-contain" />
        {figure.caption && (
          <DialogDescription className="max-h-32 overflow-y-auto text-xs leading-relaxed text-zinc-600">{figure.caption}</DialogDescription>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** Rows shown before "N more": where a long table folds (all the Free plan shows). */
const FOLD_ROWS = 50;

const rank = (row: FastRow) => row.evidence_score ?? -Infinity;
const byEvidence = (rows: FastRow[]) => [...rows].sort((a, b) => rank(b) - rank(a));

/** Rows already in the table whose values the incoming copies change (a check fixed them). */
const changedRows = (current: FastRow[], incoming: FastRow[]) => {
  const before = new Map(current.map((r) => [r.row_key, r]));
  return incoming
    .filter((row) => {
      const old = before.get(row.row_key);
      return old && !row.flag && JSON.stringify(old.cells) !== JSON.stringify(row.cells);
    })
    .map((row) => row.row_key);
};

/** New rows go in at their place in the Evidence API's order, after rows of the same score. A row a check flags as
 * not answering the question is removed. */
const upsertRows = (current: FastRow[], incoming: FastRow[]) => {
  const next = [...current];
  for (const sent of incoming) {
    const at = next.findIndex((r) => r.row_key === sent.row_key);
    if (sent.flag) {
      if (at >= 0) next.splice(at, 1);
      continue;
    }
    const row = at >= 0 && sent.figures_pending ? { ...sent, figures: { ...next[at].figures, ...sent.figures } } : sent;
    if (at >= 0) {
      next[at] = row;
      continue;
    }
    const before = next.findIndex((r) => rank(r) < rank(row));
    next.splice(before < 0 ? next.length : before, 0, row);
  }
  return next;
};

/** The new-dataset page's key, beside dataset ids. */
const NEW = "new";

type PageView = {
  question: string;
  /** The request box as the user left it. */
  draft: string;
  plan: FastPlan | null;
  rows: FastRow[];
  status: FastStatus | "idle";
  stage: string | null;
  step: string | null;
  /** The records search's live counts and most promising titles, while it runs. */
  progress: SearchProgress | null;
  stats: Partial<FastStats>;
  hiddenRows: number;
  premium: boolean;
  error: string | null;
  startedAt: number | null;
  sort: Sort | null;
  filters: Filters;
  scroll: number;
  /** The plan request the new-dataset page waits for. */
  planning?: number;
};

const blankView = (question = ""): PageView => ({
  question,
  draft: question,
  plan: null,
  rows: [],
  status: "idle",
  stage: null,
  step: null,
  progress: null,
  stats: {},
  hiddenRows: 0,
  premium: true,
  error: null,
  startedAt: null,
  sort: null,
  filters: {},
  scroll: 0,
});

const storedView = (d: FastDataset) => ({
  question: d.question,
  plan: d.plan,
  rows: byEvidence(d.rows.filter((r) => !r.flag)),
  hiddenRows: d.hidden_rows ?? 0,
  premium: d.premium ?? true,
  status: d.status,
  stage: d.stage,
  step: d.message,
  progress: null,
  stats: d.stats,
  error: d.error,
});

/**
 * Each page as last shown in this tab, kept current by the builds still streaming to it: going back to a
 * dataset shows it at once, as it was left.
 */
const pageViews = new Map<string, PageView>();
/** Datasets whose build streams to this tab. */
const streaming = new Set<string>();
let planSeq = 0;

const applied = (v: PageView, event: FastEvent): PageView => {
  switch (event.event) {
    case "status":
      return { ...v, stage: event.stage, step: event.message, progress: event.progress ?? v.progress };
    case "plan":
      return { ...v, plan: event.plan };
    case "rows":
      return { ...v, rows: upsertRows(v.rows, event.rows) };
    case "done":
      return { ...v, stats: event.stats, status: "done", stage: null };
    case "error":
      return { ...v, error: event.message, status: "failed" };
    default:
      return v;
  }
};

/** How a build's stream stopped: after its done or error event, cut without one, or turned down before it began. */
type StreamEnd = "ended" | "cut" | "refused";
/** The mounted page: the page it shows (null while it switches), and how it takes a build's events. */
type Viewer = { shows: () => string | null; event: (event: FastEvent) => void; end: (how: StreamEnd) => void; refresh: () => void };
const viewers = new Set<Viewer>();
const viewerOf = (key: string) => [...viewers].find((v) => v.shows() === key);

const short = (question: string) => (question.length > 70 ? `${question.slice(0, 67)}...` : question);

/** The server turned the request down before any build ran (busy, a build already running, a daily limit). */
const rejected = (e: unknown): e is ApiError => e instanceof ApiError && !(e instanceof FastQuotaError);

/**
 * Follows a dataset's build stream: its events go to the page showing the dataset, else to its stored view;
 * a build that ends while the user is elsewhere says so, with a link back. A request the server turns down
 * shows its reason and puts the page back as it was (before).
 */
const follow = (
  id: string,
  start: (onEvent: (event: FastEvent) => void) => Promise<unknown>,
  failed: string,
  ctx: { open: (id: string) => void; settled: () => void },
  before: Pick<PageView, "status" | "error"> & Partial<PageView> = { status: "failed", error: failed },
) => {
  streaming.add(id);
  let ended = false;
  let refused = false;
  const emit = (event: FastEvent) => {
    if (event.event === "done" || event.event === "error") ended = true;
    const v = pageViews.get(id);
    if (v) pageViews.set(id, applied(v, event));
    viewerOf(id)?.event(event);
  };
  start(emit)
    .catch((e: unknown) => {
      if (rejected(e)) {
        refused = true;
        toast.error(e.message);
        const v = pageViews.get(id);
        if (v) pageViews.set(id, { ...v, ...before, stage: null, step: null });
        viewerOf(id)?.refresh();
        return;
      }
      emit({ event: "error", message: e instanceof FastQuotaError ? QUOTA_ERROR : e instanceof Error ? e.message : failed });
    })
    .finally(() => {
      streaming.delete(id);
      const viewer = viewerOf(id);
      if (viewer) {
        viewer.end(refused ? "refused" : ended ? "ended" : "cut");
        return;
      }
      if (refused) {
        ctx.settled();
        return;
      }
      void getFastDataset(id)
        .then((d) => {
          const v = pageViews.get(id);
          if (v) pageViews.set(id, { ...v, ...storedView(d) });
          if (viewerOf(id)) return;
          const open = { label: "Open", onClick: () => ctx.open(id) };
          if (d.status === "done") toast.success(`Dataset ready: ${short(d.question)}`, { action: open });
          else if (d.status === "failed") toast.error(`Dataset failed: ${short(d.question)}`, { action: open });
        })
        .catch(() => undefined)
        .finally(ctx.settled);
    });
};

/** "Sign in to …", for a logged-out visitor: the way back is this page. */
const SignInPrompt = ({ to, next }: { to: string; next: string }) => (
  <span className="text-zinc-600">
    <Link to={loginHref(next)} className="font-medium text-zinc-900 underline underline-offset-2">Sign in</Link> {to}
  </span>
);

/** Fast datasets: a question becomes a dataset on this page. With a share token, a read-only view of someone's dataset.
 * Logged out (where the route allows it), a visitor builds one dataset; everything else asks them to sign in. */
export const FastDatasetPage = ({ shareToken }: { shareToken?: string } = {}) => {
  useDocumentTitle("Datasets");
  const [params] = useSearchParams();
  const readOnly = !!shareToken;
  const datasetId = readOnly ? null : params.get("d");
  const loadKey = shareToken ?? datasetId;
  /** The page's key in this tab's stored views; none for a shared dataset. */
  const viewKey = readOnly ? null : (datasetId ?? NEW);
  const navigate = useNavigate();
  const location = useLocation();
  const [sharedAs, setSharedAs] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const anonymous = !user && !readOnly;
  const here = `${location.pathname}${location.search}`;
  /** Logged out: what the visitor last tried that needs an account. */
  const [needLogin, setNeedLogin] = useState<string | null>(null);
  /** True (and asks the visitor to sign in) when a logged-out visitor tries what needs an account. */
  const signInFirst = (to: string) => {
    if (!anonymous) return false;
    setNeedLogin(to);
    toast(`Sign in ${to}.`, { action: { label: "Sign in", onClick: () => navigate(loginHref(here)) } });
    return true;
  };
  const { data: quota } = useFastQuota(!readOnly && !anonymous);
  const { data: summaries } = useFastDatasets(!readOnly && !anonymous);
  const [autoApprove, setAutoApprove] = useAutoApprove(user?.id);
  const [question, setQuestion] = useState("");
  const [draft, setDraft] = useState("");
  const [maxRows, setMaxRows] = useState(DEFAULT_ROWS);
  const [plan, setPlan] = useState<FastPlan | null>(null);
  const [rows, setRows] = useState<FastRow[]>([]);
  const [status, setStatus] = useState<FastStatus | "idle">("idle");
  const [stage, setStage] = useState<string | null>(null);
  const [step, setStep] = useState<string | null>(null);
  const [progress, setProgress] = useState<SearchProgress | null>(null);
  const [stats, setStats] = useState<Partial<FastStats>>({});
  const [hiddenRows, setHiddenRows] = useState(0);
  const [premium, setPremium] = useState(true);
  const [unfolded, setUnfolded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Selection>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [panel, setPanel] = useState<{ key: string | null } | null>(null);
  const [pending, setPending] = useState<{ key: string | null; label: string } | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const textSize = useTextSize();
  /** Cells attached to the next chat message, and cells picked with Shift or Cmd-click to attach. */
  const [chatCells, setChatCells] = useState<ChatCell[]>([]);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [sort, setSort] = useState<Sort | null>(null);
  const [filters, setFilters] = useState<Filters>({});
  const [ranking, setRanking] = useState<string | null>(null);
  const [normalizing, setNormalizing] = useState<string | null>(null);
  const [draggingColumn, setDraggingColumn] = useState<string | null>(null);
  const dragStart = useRef<FastColumn[] | null>(null);
  useEffect(() => {
    setPicked(new Set());
    setChatCells([]);
  }, [datasetId, shareToken]);
  const [monitor, setMonitor] = useState<FastMonitor | null>(null);
  const [methods, setMethods] = useState<FastMethods>({ methods: null, methods_status: null });
  const [methodsOpen, setMethodsOpen] = useState(false);
  useEffect(() => {
    if (anonymous || !datasetId || methods.methods_status !== "writing") return;
    const timer = window.setInterval(() => {
      getFastMethods(datasetId).then(setMethods).catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [anonymous, datasetId, methods.methods_status]);
  /** The page whose data the state holds: it trails the URL until the page switches. */
  const showing = useRef<string | null>(null);
  const [shownKey, setShownKey] = useState<string | null>(null);
  const viewing = useRef(viewKey);
  viewing.current = viewKey;
  const scroller = useRef<HTMLDivElement>(null);
  /** The dataset a broaden search started on, and its rows then. */
  const broadenFrom = useRef<{ id: string; rows: number } | null>(null);
  const [broadened, setBroadened] = useState<{ id: string; rows: number } | null>(null);
  const editor = useRef<HTMLTextAreaElement>(null);
  const rowsRef = useRef<FastRow[]>([]);
  rowsRef.current = rows;
  const [flashing, setFlashing] = useState<Set<string>>(() => new Set());
  const flash = useCallback((keys: string[]) => {
    if (!keys.length) return;
    setFlashing((current) => new Set([...current, ...keys]));
    window.setTimeout(
      () => setFlashing((current) => new Set([...current].filter((k) => !keys.includes(k)))),
      FLASH_MS,
    );
  }, []);

  const show = useCallback((key: string, v: PageView) => {
    showing.current = key;
    setShownKey(key);
    setQuestion(v.question);
    setDraft(v.draft);
    setPlan(v.plan);
    setRows(v.rows);
    setStatus(v.status);
    setStage(v.stage);
    setStep(v.step);
    setProgress(v.progress);
    setStats(v.stats);
    setHiddenRows(v.hiddenRows);
    setPremium(v.premium);
    setError(v.error);
    setStartedAt(v.startedAt);
    setSort(v.sort);
    setFilters(v.filters);
  }, []);

  useEffect(() => {
    if (!viewKey || shownKey !== viewKey) return;
    const before = pageViews.get(viewKey);
    pageViews.set(viewKey, {
      ...before,
      question,
      draft,
      plan,
      rows,
      status,
      stage,
      step,
      progress,
      stats,
      hiddenRows,
      premium,
      error,
      startedAt,
      sort,
      filters,
      scroll: before?.scroll ?? 0,
    });
  }, [viewKey, shownKey, question, draft, plan, rows, status, stage, step, progress, stats, hiddenRows, premium, error, startedAt, sort, filters]);

  const running = status === "running";
  const [endedAt, setEndedAt] = useState<number | null>(null);
  useEffect(() => {
    setEndedAt(running ? null : Date.now());
  }, [running]);

  const onEvent = useCallback(
    (event: FastEvent) => {
      if (event.event === "status") {
        setStage(event.stage);
        setStep(event.message);
        if (event.progress) setProgress(event.progress);
      } else if (event.event === "plan") setPlan(event.plan);
      else if (event.event === "rows") {
        flash(changedRows(rowsRef.current, event.rows));
        setRows((current) => upsertRows(current, event.rows));
      }
      else if (event.event === "done") {
        setStats(event.stats);
        setStatus("done");
        setStage(null);
        setMethods((current) => ({ ...current, methods_status: "writing" }));
      } else if (event.event === "error") {
        setError(event.message);
        setStatus("failed");
      }
    },
    [flash],
  );

  const applyDataset = useCallback((dataset: FastDataset) => {
    setPlan(dataset.plan);
    setRows(byEvidence(dataset.rows.filter((r) => !r.flag)));
    setHiddenRows(dataset.hidden_rows ?? 0);
    setStatus(dataset.status);
    setStage(dataset.stage);
    setStep(dataset.message);
    setStats(dataset.stats);
    setError(dataset.error);
    setMonitor(monitorOf(dataset));
    setMethods(methodsOf(dataset));
  }, []);

  const reloadTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(reloadTimer.current), [datasetId]);
  const reloadRows = useCallback(() => {
    if (!datasetId) return;
    window.clearTimeout(reloadTimer.current);
    getFastDataset(datasetId)
      .then((dataset) => {
        applyDataset(dataset);
        if (dataset.status === "running") reloadTimer.current = window.setTimeout(reloadRows, RUNNING_POLL_MS);
      })
      .catch(() => {});
  }, [datasetId, applyDataset]);

  const finish = useCallback((how: StreamEnd) => {
    const id = showing.current;
    if (how === "refused") broadenFrom.current = null;
    void queryClient.invalidateQueries({ queryKey: fastDatasetsKey });
    if (id && id !== NEW)
      void getFastDataset(id)
        .then((d) => {
          if (showing.current !== id) return;
          if (how === "cut") {
            // No done event: the stored dataset says whether the build finished, failed or is still going.
            applyDataset(d);
            if (d.status === "running") {
              reloadTimer.current = window.setTimeout(reloadRows, RUNNING_POLL_MS);
              return;
            }
          }
          setMonitor(monitorOf(d));
          setMethods(methodsOf(d));
          // The stream stops sending rows a free plan hides; the stored dataset says which rows it sees.
          if (d.premium === false) setRows(byEvidence(d.rows.filter((r) => !r.flag)));
          setHiddenRows(d.hidden_rows ?? 0);
          setPremium(d.premium ?? true);
          if (broadenFrom.current?.id === id) {
            const found = Math.max(0, d.rows.filter((r) => !r.flag).length + (d.hidden_rows ?? 0) - broadenFrom.current.rows);
            broadenFrom.current = null;
            setBroadened({ id, rows: found });
            if (found) toast.success(`Broaden search found ${found} more ${found === 1 ? "row" : "rows"}.`);
            else toast("Broaden search found no new rows.");
          }
        })
        .catch(() => {
          if (how !== "cut" || showing.current !== id) return;
          setError("The connection to the build was lost. Reload the page to see where it is.");
          setStatus("failed");
        });
  }, [queryClient, applyDataset, reloadRows]);

  useEffect(() => {
    const viewer: Viewer = {
      shows: () => (viewing.current && showing.current === viewing.current ? viewing.current : null),
      event: onEvent,
      end: finish,
      refresh: () => {
        const key = showing.current;
        const v = key ? pageViews.get(key) : undefined;
        if (key && v) show(key, v);
      },
    };
    viewers.add(viewer);
    return () => {
      viewers.delete(viewer);
    };
  }, [onEvent, finish, show]);

  const followCtx = useMemo(
    () => ({
      open: (id: string) => navigate(`${FAST_ROUTE}?d=${id}`),
      settled: () => void queryClient.invalidateQueries({ queryKey: fastDatasetsKey }),
    }),
    [navigate, queryClient],
  );

  // Logged out, the new-dataset page opens the dataset this browser already built.
  useEffect(() => {
    if (!anonymous || datasetId || pageViews.get(NEW)?.planning) return;
    const claim = readAnonClaim();
    if (claim && !streaming.has(claim.id)) navigate(`${FAST_ROUTE}?d=${claim.id}`, { replace: true });
  }, [anonymous, datasetId, navigate]);

  // Signed in: the dataset built logged out in this browser moves to the account, then shows as the account's.
  useEffect(() => {
    if (!user) return;
    const claim = readAnonClaim();
    if (!claim) return;
    let cancelled = false;
    let timer: number | undefined;
    const attempt = () =>
      claimAnonDataset(claim.token)
        .then(({ id }) => {
          clearAnonClaim();
          void queryClient.invalidateQueries({ queryKey: fastDatasetsKey });
          if (cancelled) return;
          toast.success("The dataset you built is saved to your account.");
          navigate(`${FAST_ROUTE}?d=${id}`, { replace: true });
        })
        .catch((e: unknown) => {
          if (cancelled || !(e instanceof ApiError)) return;
          // 409: the build is still starting; 404: claimed already, or the token no longer reads it.
          if (e.status === 409) timer = window.setTimeout(attempt, REVIEW_POLL_MS);
          else if (e.status === 404) clearAnonClaim();
        });
    attempt();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [user, navigate, queryClient]);

  /** Plans a dataset on the new-dataset page; the plan opens for review there, or waits in the list if the user left. */
  const build = (text: string) => {
    const q = text.trim();
    if (q.length < 8 || running) return;
    const token = ++planSeq;
    const planning: PageView = { ...blankView(q), status: "running", stage: "plan", startedAt: Date.now(), planning: token };
    pageViews.set(NEW, planning);
    setMethods({ methods: null, methods_status: null });
    setSelected(null);
    if (viewKey === NEW) show(NEW, planning);
    else navigate(FAST_ROUTE);
    const input = { question: q, max_rows: allowedRows(maxRows, quota?.max_rows), papers: null };
    if (anonymous || autoApprove) {
      buildApproved(q, input, token);
      return;
    }
    planFastDataset(input)
      .then((dataset) => {
        pageViews.set(dataset.id, { ...blankView(q), plan: dataset.plan, status: "review" });
        void queryClient.invalidateQueries({ queryKey: fastDatasetsKey });
        const mine = pageViews.get(NEW)?.planning === token;
        if (mine) pageViews.delete(NEW);
        if (mine && viewerOf(NEW)) navigate(`${FAST_ROUTE}?d=${dataset.id}`, { replace: true });
        else
          toast.success(`Columns ready to review: ${short(q)}`, {
            action: { label: "Open", onClick: () => navigate(`${FAST_ROUTE}?d=${dataset.id}`) },
          });
      })
      .catch(planFailed(q, token));
  };

  const planFailed = (q: string, token: number) => (e: unknown) => {
    if (e instanceof FastQuotaError) void queryClient.invalidateQueries({ queryKey: fastQuotaKey });
    const message = e instanceof FastQuotaError ? QUOTA_ERROR : e instanceof Error ? e.message : "The dataset API did not answer.";
    if (anonymous && e instanceof ApiError && e.status === 429 && /log in/i.test(e.message)) setNeedLogin("to build another dataset");
    const now = pageViews.get(NEW);
    if (now?.planning !== token) return;
    // A request the server turned down leaves the question in the box, to build again later.
    pageViews.set(NEW, rejected(e) ? blankView(q) : { ...now, status: "failed", stage: null, error: message, planning: undefined });
    const viewer = viewerOf(NEW);
    if (viewer) viewer.refresh();
    if (rejected(e)) toast.error(message);
    else if (!viewer) toast.error(`Dataset failed: ${short(q)}`);
  };

  /** Auto-approve: the server approves the proposed columns and builds within the stream, which the page follows
   * from the dataset's first event as it follows its own Build. */
  const buildApproved = (q: string, input: { question: string; max_rows: number; papers: null }, token: number) => {
    const early: FastEvent[] = [];
    let forward: ((event: FastEvent) => void) | null = null;
    let opened = false;
    const start = (on: (event: FastEvent) => void) => (anonymous ? streamAnonBuild(q, on) : streamFastAutoBuild(input, on));
    const done: Promise<unknown> = start((event) => {
      if (event.event !== "dataset" || opened) {
        if (forward) forward(event);
        else early.push(event);
        return;
      }
      opened = true;
      const id = event.id;
      pageViews.set(id, { ...blankView(q), status: "running", stage: "plan", startedAt: Date.now() });
      void queryClient.invalidateQueries({ queryKey: fastDatasetsKey });
      follow(
        id,
        (emit) => {
          forward = emit;
          early.splice(0).forEach(emit);
          return done;
        },
        "The build failed.",
        followCtx,
      );
      const mine = pageViews.get(NEW)?.planning === token;
      if (mine) pageViews.delete(NEW);
      if (mine && viewerOf(NEW)) navigate(`${FAST_ROUTE}?d=${id}`, { replace: true });
      else toast.success(`Building with the proposed columns: ${short(q)}`, { action: { label: "Open", onClick: () => navigate(`${FAST_ROUTE}?d=${id}`) } });
    });
    done.catch((e: unknown) => {
      if (!opened) planFailed(q, token)(e);
    });
  };

  /** Builds the dataset in review from the columns as the user left them. */
  const approve = (edited: ReviewedColumn[], sources: ReviewedSources | null) => {
    if (!datasetId || running) return;
    const before = { status: "review" as const, error: null, plan };
    setPlan((current) => {
      if (!current) return current;
      const before = new Map(current.columns.map((c) => [c.key, c]));
      return {
        ...current,
        columns: edited.map((c, i): FastColumn => ({
          scope: "study",
          role: "context",
          ...(c.key ? before.get(c.key) : undefined),
          key: c.key ?? `new-${i}`,
          label: c.label,
          question: c.description || c.label,
          value_type: c.value_type ?? "text",
          options: c.options ?? [],
        })),
      };
    });
    setStatus("running");
    setError(null);
    setStage("search");
    setStep(null);
    setProgress(null);
    setStartedAt(Date.now());
    follow(datasetId, (on) => streamFastReviewedBuild(datasetId, edited, sources, on), "The build did not start.", followCtx, before);
  };

  const more = (broaden = false) => {
    if (!datasetId || running || signInFirst(broaden ? "to broaden the search" : "to add more rows")) return;
    const before = { status, error };
    setStatus("running");
    setError(null);
    setStage("search");
    setStep(broaden ? "Searching for documents the dataset is missing" : null);
    setProgress(null);
    broadenFrom.current = broaden ? { id: datasetId, rows: rows.length + hiddenRows } : null;
    setBroadened(null);
    setStartedAt(Date.now());
    follow(
      datasetId,
      (on) => (broaden ? streamFastBroaden : streamFastMore)(datasetId, on),
      broaden ? "Broaden search failed." : "More rows failed.",
      followCtx,
      before,
    );
  };

  // Open a page: as this tab last showed it, at once, then as stored (following a build running elsewhere).
  useEffect(() => {
    setSelected(null);
    setUnfolded(false);
    setPanel(null);
    const key = viewKey ?? shareToken ?? NEW;
    const cached = readOnly ? undefined : pageViews.get(key);
    const listed = datasetId
      ? queryClient.getQueryData<FastDatasetSummary[]>(fastDatasetsKey)?.find((d) => d.id === datasetId)
      : undefined;
    show(key, cached ?? blankView(listed?.question ?? ""));
    if (!loadKey) {
      setMethods({ methods: null, methods_status: null });
      setMonitor(null);
    }
    const scroll = cached?.scroll ?? 0;
    window.requestAnimationFrame(() => {
      if (scroller.current) scroller.current.scrollTop = scroll;
    });
    const keepScroll = () => {
      const v = pageViews.get(key);
      if (v && scroller.current && showing.current === key) pageViews.set(key, { ...v, scroll: scroller.current.scrollTop });
    };
    if (!loadKey) return keepScroll;
    let cancelled = false;
    let timer: number | undefined;
    const load = async () => {
      try {
        const dataset = shareToken ? await getSharedFastDataset(shareToken) : await getFastDataset(loadKey);
        if (cancelled) return;
        const before = readOnly ? undefined : pageViews.get(key);
        const stored = storedView(dataset);
        show(key, {
          ...(before ?? blankView()),
          ...stored,
          // Rows a build streaming to this tab sent may not be stored yet.
          rows: before && streaming.has(loadKey) ? upsertRows(stored.rows, before.rows) : stored.rows,
          draft: before?.draft || dataset.question,
        });
        setSharedAs(dataset.share_token ?? null);
        setMonitor(readOnly ? null : monitorOf(dataset));
        setMethods(methodsOf(dataset));
        if (dataset.status === "running" && !streaming.has(loadKey)) timer = window.setTimeout(load, RUNNING_POLL_MS);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Could not load this dataset.");
          setStatus("failed");
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      keepScroll();
    };
  }, [loadKey, shareToken]);

  // Columns in review can be approved elsewhere (`paperclip dataset approve`, another tab): the page follows.
  useEffect(() => {
    if (readOnly || !datasetId || status !== "review") return;
    const timer = window.setInterval(() => {
      getFastProgress(datasetId)
        .then((p) => {
          if (p.status === "review" || showing.current !== datasetId) return;
          setStartedAt(Date.now());
          reloadRows();
          void queryClient.invalidateQueries({ queryKey: fastDatasetsKey });
          if (p.status === "running") toast(p.auto_approved ? "The columns were auto-approved; building." : "The columns were approved; building.");
        })
        .catch(() => undefined);
    }, REVIEW_POLL_MS);
    return () => window.clearInterval(timer);
  }, [readOnly, datasetId, status, reloadRows, queryClient]);
  const summary = summaries?.find((d) => d.id === datasetId);
  const autoApproved = !!summary?.settings?.auto_approved;
  /** More rows and Broaden search continue a build that finished, or one that failed after it started. */
  const continuable = !!datasetId && !readOnly && (status === "done" || (status === "failed" && !!summary?.build_id));
  const empty = status === "done" && rows.length === 0 && hiddenRows === 0;
  /** Puts a request in the box to edit; nothing builds until the user presses Build. */
  const fillRequest = (text: string) => {
    setDraft(text);
    window.requestAnimationFrame(() => {
      const box = editor.current;
      if (!box) return;
      box.focus();
      box.setSelectionRange(box.value.length, box.value.length);
      box.scrollIntoView({ block: "nearest" });
    });
  };

  const columns = useMemo(() => {
    const all = plan?.columns ?? [];
    if (status !== "done" || rows.length === 0) return all;
    return all.filter(
      (column) => column.key.startsWith("u_") || rows.some((row) => row.cells[column.key] || row.figures?.[column.key]),
    );
  }, [plan, rows, status]);

  const putColumn = (column: FastColumn, cells: Record<string, FastCell>, figures: Record<string, FastFigure> = {}) => {
    setPlan((current) => {
      if (!current) return current;
      const at = current.columns.findIndex((c) => c.key === column.key);
      const next = [...current.columns];
      if (at >= 0) next[at] = column;
      else next.push(column);
      return { ...current, columns: next };
    });
    setRows((current) =>
      current.map((row) => {
        const { [column.key]: _old, ...rest } = row.cells;
        const { [column.key]: _oldFigure, ...otherFigures } = row.figures ?? {};
        const figure = figures[row.row_key];
        return {
          ...row,
          cells: cells[row.row_key] ? { ...rest, [column.key]: cells[row.row_key] } : rest,
          figures: figure ? { ...otherFigures, [column.key]: figure } : otherFigures,
        };
      }),
    );
  };

  const saveColumn = async (key: string | null, draft: ColumnDraft) => {
    if (!datasetId || signInFirst("to edit columns")) return;
    setPanel(null);
    setPending({ key, label: draft.label });
    try {
      const { column, cells, figures } = key
        ? await updateFastColumn(datasetId, key, draft)
        : await addFastColumn(datasetId, draft);
      putColumn(column, cells, figures);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "The column could not be saved.");
    } finally {
      setPending(null);
    }
  };

  const deleteRow = async (rowKey: string) => {
    if (!datasetId) return;
    const before = rows;
    setRows((current) => current.filter((r) => r.row_key !== rowKey));
    setSelected(null);
    try {
      await deleteFastRows(datasetId, [rowKey]);
    } catch (e) {
      setRows(before);
      toast.error(e instanceof Error ? e.message : "The row could not be deleted.");
    }
  };

  const clearCell = async (rowKey: string, key: string) => {
    if (!datasetId) return;
    const before = rows;
    setRows((current) =>
      current.map((r) => {
        if (r.row_key !== rowKey) return r;
        const { [key]: _gone, ...cells } = r.cells;
        return { ...r, cells };
      }),
    );
    setSelected((current) => (current ? { ...current, columnKey: null } : current));
    try {
      await clearFastCells(datasetId, [{ row_key: rowKey, key }]);
    } catch (e) {
      setRows(before);
      toast.error(e instanceof Error ? e.message : "The value could not be cleared.");
    }
  };

  const checkNow = () => {
    if (!datasetId || running || signInFirst("to update this dataset")) return;
    const before = { status, error };
    setStatus("running");
    setError(null);
    setStage("search");
    setStep("Updating: searching for new documents");
    setProgress(null);
    setStartedAt(Date.now());
    follow(datasetId, (on) => streamFastMonitorRun(datasetId, on), "The check for new documents failed.", followCtx, before);
  };

  const copyShareLink = async () => {
    if (!datasetId || signInFirst("to share this dataset")) return;
    try {
      const token = sharedAs ?? (await shareFastDataset(datasetId)).token;
      setSharedAs(token);
      await navigator.clipboard.writeText(sharedFastUrl(token));
      toast.success("Link copied: anyone signed in to Paperclip can view this dataset.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "The dataset could not be shared.");
    }
  };

  const stopSharing = async () => {
    if (!datasetId) return;
    try {
      await unshareFastDataset(datasetId);
      setSharedAs(null);
      toast.success("The link no longer works.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Sharing could not be stopped.");
    }
  };

  /** Opens the methods; one that is missing (built before methods existed) or failed is written first. */
  const viewMethods = async () => {
    if (signInFirst("to view the methods")) return;
    if (methods.methods_status === "ready" && methods.methods) {
      setMethodsOpen(true);
      return;
    }
    if (!datasetId) return;
    try {
      await writeFastMethods(datasetId);
      setMethods((current) => ({ ...current, methods_status: "writing" }));
      toast("Writing the methods section. View Methods turns on when it is ready.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "The methods could not be written.");
    }
  };

  const withImages = rows.some((row) => Object.keys(row.figures ?? {}).length > 0);
  const [exporting, setExporting] = useState(false);
  const exportCsv = () => {
    if (signInFirst("to export this dataset")) return;
    const target = shareToken ? { token: shareToken } : datasetId ? { id: datasetId } : null;
    if (!target) return;
    setExporting(true);
    downloadFastCsv(target, question, withImages)
      .catch((e: unknown) => toast.error(e instanceof Error ? e.message : "The export could not be downloaded."))
      .finally(() => setExporting(false));
  };

  const removeColumn = async (key: string) => {
    if (!datasetId) return;
    const label = plan?.columns.find((c) => c.key === key)?.label ?? "this column";
    if (!window.confirm(`Delete ${label}? Its values are removed from every row.`)) return;
    setPanel(null);
    try {
      await deleteFastColumn(datasetId, key);
      setPlan((current) => (current ? { ...current, columns: current.columns.filter((c) => c.key !== key) } : current));
      setSelected((current) => (current?.columnKey === key ? { ...current, columnKey: null } : current));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "The column could not be deleted.");
    }
  };

  const editable = status === "done" && !!datasetId && !pending && !anonymous;
  /** Logged out, a finished dataset's edits are drawn as they are and ask the visitor to sign in. */
  const signInToEdit = anonymous && status === "done" && !!datasetId;
  const reorderable = editable && !readOnly && columns.length > 1;

  /** Moves a column to where another one is, live while dragging. */
  const moveColumn = (from: string, to: string) =>
    setPlan((current) => {
      if (!current) return current;
      const next = [...current.columns];
      const i = next.findIndex((c) => c.key === from);
      const j = next.findIndex((c) => c.key === to);
      if (i < 0 || j < 0 || i === j) return current;
      next.splice(j, 0, ...next.splice(i, 1));
      return { ...current, columns: next };
    });

  const dropColumn = async () => {
    const before = dragStart.current;
    dragStart.current = null;
    setDraggingColumn(null);
    if (!datasetId || !plan || !before) return;
    const keys = plan.columns.map((c) => c.key);
    if (keys.join("\n") === before.map((c) => c.key).join("\n")) return;
    try {
      await reorderFastColumns(datasetId, keys);
    } catch (e) {
      setPlan((current) => (current ? { ...current, columns: before } : current));
      toast.error(e instanceof Error ? e.message : "The column order could not be saved.");
    }
  };

  /** Values that appeared or changed since the last rows, after the first rows of a dataset: they fade in. */
  const seenValues = useRef<{ key: string | null; values: Map<string, string> } | null>(null);
  const freshCells = useMemo(() => {
    const fresh = new Set<string>();
    const seen = seenValues.current;
    if (!seen || seen.key !== loadKey) return fresh;
    for (const row of rows) {
      for (const [key, cell] of Object.entries(row.cells)) {
        const id = chatCellKey(row.row_key, key);
        if (seen.values.get(id) !== cellText(cell)) fresh.add(id);
      }
    }
    return fresh;
  }, [rows, loadKey]);
  useEffect(() => {
    const values = new Map<string, string>();
    for (const row of rows) for (const [key, cell] of Object.entries(row.cells)) values.set(chatCellKey(row.row_key, key), cellText(cell));
    seenValues.current = { key: loadKey, values };
  }, [rows, loadKey]);
  /** Cells can be attached to the chat of a finished dataset, the owner's or a shared one. */
  const chattable = status === "done" && (!!datasetId || readOnly) && !anonymous;
  const togglePicked = (rowKey: string, columnKey: string) =>
    setPicked((current) => {
      const next = new Set(current);
      const key = chatCellKey(rowKey, columnKey);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const chatCellOf = (row: FastRow, key: string): ChatCell => {
    const cell = row.cells[key];
    return {
      rowKey: row.row_key,
      rowLabel: paperName(row),
      columnKey: key,
      columnLabel: columns.find((c) => c.key === key)?.label ?? key,
      value: cell ? cellText(cell) : "",
      quote: cell ? (cell.parts.length ? cell.parts.map((p) => p.text).join(" / ") : cell.text) : "",
    };
  };
  const addToChat = (cells: ChatCell[]) => {
    setChatCells((current) => withCells(current, cells));
    setChatOpen(true);
  };
  const addPickedToChat = () => {
    const byKey = new Map(rows.map((r) => [r.row_key, r]));
    const cells: ChatCell[] = [];
    for (const row of shown) {
      for (const column of columns) {
        if (picked.has(chatCellKey(row.row_key, column.key))) cells.push(chatCellOf(byKey.get(row.row_key) ?? row, column.key));
      }
    }
    addToChat(cells);
    setPicked(new Set());
  };

  const building = running && !plan;
  const live = running && startedAt !== null;
  const checking = running && (stage === "check" || stage === "final");
  const skeletons = !running || checking ? 0 : rows.length === 0 ? 6 : 2;
  const panelColumn = panel?.key ? columns.find((c) => c.key === panel.key) ?? null : null;
  const selectedRow = useMemo(
    () => (selected ? rows.find((r) => r.row_key === selected.rowKey) ?? null : null),
    [rows, selected],
  );
  const papersSeen = new Set(rows.map((r) => r.source.doc_id)).size;
  const shown = useMemo(() => view(rows, columns, sort, filters), [rows, columns, sort, filters]);
  const folded = !unfolded && shown.length > FOLD_ROWS;
  const filtered = Object.keys(filters).length > 0;
  const paperFields: FieldMenu[] = PAPER_FIELDS.map((f) => ({ key: f.key, label: f.label, kind: f.kind, values: f.key === "_paper" || f.key === "_evidence" ? [] : filterValues(rows, f.key, f.kind) }));
  const columnField = (column: FastColumn): FieldMenu => {
    const kind = kindOf(column);
    return {
      key: column.key,
      label: column.label,
      kind,
      values: filterValues(rows, column.key, kind),
      smart: kind === "text" && datasetId && !readOnly && status === "done" ? { placeholder: `greater in ${column.label}` } : undefined,
      onNormalize: kind === "text" && datasetId && !readOnly && status === "done"
        ? () => void (signInFirst("to clean up a column's values") || setNormalizing(column.key))
        : undefined,
    };
  };
  const setFilter = (key: string, values: string[] | null) =>
    setFilters((current) => {
      const { [key]: _old, ...rest } = current;
      return values === null ? rest : { ...rest, [key]: values };
    });
  const smartSort = async (key: string, criterion: string) => {
    if (!datasetId || signInFirst("to rank by a column")) return;
    setRanking(key);
    try {
      const found = await rankFastColumn(datasetId, key, criterion);
      setSort({ key, dir: "desc", smart: { order: found.order, criterion: found.criterion } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "The column could not be ranked.");
    } finally {
      setRanking(null);
    }
  };
  const sortMark = (key: string) =>
    sort?.key === key ? (
      sort.dir === "asc" ? <ArrowUp className="h-3 w-3 shrink-0" aria-label="Sorted low to high" /> : <ArrowDown className="h-3 w-3 shrink-0" aria-label="Sorted high to low" />
    ) : null;
  const menuProps = { sort, filters, ranking, onSort: setSort, onSmart: (k: string, c: string) => void smartSort(k, c), onFilter: setFilter };
  return (
    <div className="flex min-h-0" style={{ zoom: PAGE_ZOOM, height: `${100 / PAGE_ZOOM}%` }}>
      <div ref={scroller} className="min-w-0 flex-1 overflow-y-auto overscroll-y-contain [scrollbar-gutter:stable]">
        <div className="w-full pb-24 pl-4 pr-6 pt-6">
          {readOnly && (
            <div className="border-b border-zinc-200 pb-3">
              <p className="font-ui text-2xs uppercase tracking-wider text-zinc-400">Shared dataset · view only; you can ask the chat about it</p>
              <h1 className="mt-1 text-[17px] leading-snug text-zinc-900">{question}</h1>
            </div>
          )}
          {!readOnly && <h1 className="font-display mb-5 text-4xl leading-none tracking-tight text-zinc-900">Datasets</h1>}
          {!readOnly && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              build(editor.current?.value ?? "");
            }}
            className="rounded-[3px] border border-zinc-300 bg-white focus-within:border-zinc-500"
          >
            <label htmlFor="fast-question" className="sr-only">Dataset request</label>
            <textarea
              id="fast-question"
              ref={editor}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              placeholder="Describe the dataset you want, e.g. Build a dataset of HbA1c reductions with tirzepatide versus placebo, by dose"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  build(e.currentTarget.value);
                }
              }}
              className="block w-full resize-none border-0 bg-transparent px-4 pt-3 text-[15px] leading-6 text-zinc-900 outline-none placeholder:text-zinc-400"
            />
            <div className="font-ui flex flex-wrap items-center justify-end gap-3 px-4 pb-2.5 pt-1 text-xs text-zinc-500">
              {anonymous ? (
                <span className="mr-auto text-zinc-400">One dataset of up to 50 rows without signing in</span>
              ) : (
                <>
                  <BuildOptions
                    rows={maxRows}
                    maxRows={quota?.max_rows}
                    onRows={setMaxRows}
                    autoApprove={autoApprove}
                    onAutoApprove={setAutoApprove}
                    disabled={running}
                  />
                  <QuotaLine quota={quota} />
                </>
              )}
              <Button type="submit" size="sm" disabled={running} aria-label="Build dataset">
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
                Build
              </Button>
            </div>
          </form>
          )}
          {anonymous && (needLogin || (datasetId && status !== "idle")) && (
            <p className="font-ui mt-2 text-xs" role="status">
              <SignInPrompt
                next={here}
                to={needLogin ?? "to keep this dataset and build another, or to add rows and columns, chat with it, export or share it."}
              />
              {needLogin && "."}
            </p>
          )}

          {status === "idle" && !!loadKey && (
            <p className="font-ui mt-6 flex items-center gap-2 text-xs text-zinc-400" role="status">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Loading
            </p>
          )}
          {status === "idle" && !readOnly && !loadKey && (
            <div className="mt-6 flex flex-wrap gap-2">
              {SAMPLES.map((sample) => (
                <button
                  key={sample}
                  type="button"
                  onClick={() => fillRequest(sample)}
                  className="rounded-full border border-zinc-200 px-3 py-1 text-[13px] text-zinc-600 hover:border-zinc-400 hover:text-zinc-900"
                >
                  {sample}
                </button>
              ))}
            </div>
          )}
          {status === "idle" && !readOnly && !loadKey && <DatasetsFaq />}

          {status === "review" && plan && datasetId && <BuildStages stage={null} message={null} startedAt={null} paused />}
          {status === "review" && plan && datasetId && (
            <ColumnReview key={datasetId} columns={plan.columns} fixedKeys={fixedColumns(plan)} describe={describe} busy={false} draftKey={datasetId} sources={plannedSources(plan.planner_state)} onBuild={approve} />
          )}

          {status !== "idle" && status !== "review" && (
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-zinc-500" role="status" aria-live="polite">
              {running && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" aria-hidden />}
              <span><b className="font-semibold text-zinc-800">{rows.length}</b> {rows.length === 1 ? "row" : "rows"}</span>
              {autoApproved && !readOnly && <span>Columns auto-approved</span>}
              {(stats.papers_read || papersSeen > 0) && (
                <span>
                  <b className="font-semibold text-zinc-800">{status === "done" ? stats.papers_read : papersSeen}</b>{" "}
                  {status === "done"
                    ? `${stats.papers_read === 1 ? "document" : "documents"} read`
                    : papersSeen === 1 ? "document with rows" : "documents with rows"}
                  {status === "done" && !!stats.documents_considered && (
                    <>
                      {" "}of <b className="font-semibold text-zinc-800">{Math.max(stats.documents_considered, stats.papers_read ?? 0).toLocaleString()}</b>{" "}
                      considered
                    </>
                  )}
                </span>
              )}
              <Elapsed startedAt={startedAt} endedAt={running ? null : endedAt} fallbackMs={stats.ms ?? 0} />
              {status === "done" && !!stats.searches?.length && (
                <span title={stats.searches.join("\n")}>
                  <b className="font-semibold text-zinc-800">{stats.searches.length}</b> new {stats.searches.length === 1 ? "search" : "searches"}
                </span>
              )}
              {status === "done" && broadened?.id === datasetId && (
                <span>
                  <b className="font-semibold text-zinc-800">+{broadened.rows}</b> {broadened.rows === 1 ? "row" : "rows"} from broaden search
                </span>
              )}
              {status === "done" && rows.length > 0 && (
                <span>Click a row to see the spans it came from{chattable ? "; Shift-click cells to ask the chat about them" : ""}.</span>
              )}
              {status === "failed" &&
                (error === QUOTA_ERROR ? (
                  quota ? <span className="text-zinc-700"><QuotaReached quota={quota} /></span> : null
                ) : (
                  <span className="text-red-700">{error && !continuable ? withoutMoreRows(error) : error}</span>
                ))}
              {(datasetId || readOnly) && rows.length > 0 && !running && (
                <div className="ml-auto flex items-center gap-2">
                  {continuable && (
                    <Button size="sm" variant="outline" onClick={() => more(true)} title="Look at what was found and missed, and search again for relevant documents the dataset does not have">
                      <ScanSearch className="h-4 w-4" aria-hidden />
                      Broaden search
                    </Button>
                  )}
                  {datasetId && monitor && status === "done" && (
                    <Button size="sm" variant="outline" onClick={checkNow} title="Run this dataset's searches again and add rows from documents it has not read">
                      <RefreshCw className="h-4 w-4" aria-hidden />
                      Update this dataset
                    </Button>
                  )}
                  {(datasetId || methods.methods_status === "ready") && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!anonymous && (methods.methods_status === "writing" || (readOnly && methods.methods_status !== "ready"))}
                      title={methods.methods_status === "writing" && !anonymous ? "Writing the methods section" : "A methods section for a publication, written from how this dataset was built"}
                      onClick={() => void viewMethods()}
                    >
                      {methods.methods_status === "writing" && !anonymous ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <FileText className="h-4 w-4" aria-hidden />}
                      View Methods
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={exportCsv}
                    disabled={exporting}
                    title={withImages ? "A ZIP with the CSV and every row's images" : undefined}
                  >
                    {exporting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Download className="h-4 w-4" aria-hidden />}
                    {withImages ? "Export CSV + images" : "Export CSV"}
                  </Button>
                  {datasetId && (
                    <Button size="sm" variant="outline" onClick={() => void copyShareLink()}>
                      <Link2 className="h-4 w-4" aria-hidden />
                      {sharedAs ? "Copy link" : "Share"}
                    </Button>
                  )}
                  {datasetId && sharedAs && (
                    <button type="button" className="text-xs text-zinc-500 underline hover:text-zinc-900" onClick={() => void stopSharing()}>
                      Stop sharing
                    </button>
                  )}
                  {(datasetId || readOnly) && !chatOpen && (
                    <Button size="sm" variant="outline" onClick={() => signInFirst("to chat with this dataset") || setChatOpen(true)}>
                      <MessageSquare className="h-4 w-4" aria-hidden />
                      Chat with this dataset
                    </Button>
                  )}
                </div>
              )}
              {status === "failed" && !rows.length && continuable && (
                <button type="button" className="text-zinc-700 underline" onClick={() => more()}>
                  More rows
                </button>
              )}
              {status === "failed" && !rows.length && !continuable && question && !readOnly && (
                <button type="button" className="text-zinc-700 underline" onClick={() => build(question)}>
                  Try again
                </button>
              )}
            </div>
          )}
          {empty && (
            <div className="mt-6 flex flex-wrap items-center gap-3 text-[13px] text-zinc-600" role="status">
              <span>{EMPTY_RESULT}</span>
              {!readOnly && (
                <Button size="sm" variant="outline" onClick={() => fillRequest(question)}>
                  Edit the question
                </Button>
              )}
            </div>
          )}
          {datasetId && !readOnly && !anonymous && status === "done" && rows.length > 0 && (
            <AgentPromptBox key={datasetId} dataset={{ id: datasetId, name: question, agent_prompt: agentPrompt(datasetId, question) }} />
          )}
          {running && <BuildStages stage={stage} message={step} startedAt={startedAt} progress={progress} />}
          {methods.methods && <MethodsDialog text={methods.methods} open={methodsOpen} onOpenChange={setMethodsOpen} />}
          {datasetId && normalizing && columns.some((c) => c.key === normalizing) && (
            <NormalizeDialog
              key={normalizing}
              datasetId={datasetId}
              column={columns.find((c) => c.key === normalizing)!}
              rows={rows}
              onClose={() => setNormalizing(null)}
              onApplied={reloadRows}
            />
          )}

          {status !== "review" && !empty && (columns.length > 0 || building) && (
            <div className="mt-4">
              {rows.length > 0 && !(status === "done" && datasetId) && (
                <div className="mb-1.5 flex justify-end">
                  <TextSizeToggle {...textSize} />
                </div>
              )}
              {status === "done" && datasetId && (
                <div className="flex items-center justify-end gap-3">
                  <TextSizeToggle {...textSize} />
                  <button
                    type="button"
                    disabled={!editable && !signInToEdit}
                    onClick={() => signInFirst("to add columns") || setPanel(panel && !panel.key ? null : { key: null })}
                    className={cn(
                      "font-ui -mb-px flex items-center gap-1 rounded-t-[3px] border border-b-0 px-3 py-1.5 text-2xs font-medium uppercase tracking-wider",
                      panel && !panel.key
                        ? "border-zinc-200 bg-zinc-50 text-zinc-900"
                        : "border-transparent text-zinc-500 hover:text-zinc-900 disabled:hover:text-zinc-500",
                    )}
                  >
                    {pending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Plus className="h-3 w-3" aria-hidden />}
                    {pending ? (pending.key ? `Rerunning ${pending.label}` : `Adding ${pending.label}`) : "Add column"}
                  </button>
                </div>
              )}
              {panel && (panel.key === null || panelColumn) && (
                <ColumnPanel
                  key={panel.key ?? "new"}
                  initial={
                    panelColumn
                      ? { label: panelColumn.label, description: describe(panelColumn), value_type: panelColumn.value_type, options: panelColumn.options ?? [] }
                      : null
                  }
                  fixed={!!panelColumn && fixedColumns(plan).has(panelColumn.key)}
                  busy={!editable}
                  onSave={(draft) => void saveColumn(panel.key, draft)}
                  onDelete={panel.key ? () => void removeColumn(panel.key!) : undefined}
                  onClose={() => setPanel(null)}
                />
              )}
              {(filtered || sort) && (
                <div className="font-ui flex items-center gap-3 py-1.5 text-2xs text-zinc-500">
                  <span>
                    {filtered ? `Showing ${shown.length} of ${rows.length} rows` : `${rows.length} rows`}
                    {sort?.smart && ` · ranked by “${sort.smart.criterion}”`}
                  </span>
                  <button
                    type="button"
                    className="text-zinc-700 underline hover:text-zinc-900"
                    onClick={() => {
                      setSort(null);
                      setFilters({});
                    }}
                  >
                    Reset to evidence order
                  </button>
                </div>
              )}
              {picked.size > 0 && (
                <div className="font-ui sticky top-0 z-20 flex items-center gap-3 border border-zinc-300 bg-white px-3 py-1.5 text-xs text-zinc-600 shadow-sm" role="status">
                  <span>
                    <b className="font-semibold text-zinc-900">{picked.size}</b> {picked.size === 1 ? "cell" : "cells"} selected
                  </span>
                  <Button size="sm" variant="outline" className="h-7" onClick={addPickedToChat}>
                    <MessageSquarePlus className="h-3.5 w-3.5" aria-hidden />
                    Add to chat
                  </Button>
                  <button type="button" className="underline hover:text-zinc-900" onClick={() => setPicked(new Set())}>
                    Clear
                  </button>
                  <span className="text-zinc-400">Shift- or ⌘-click cells to select more</span>
                </div>
              )}
              <ScrollFade>
              <table className="w-full border-collapse text-[13px]" style={{ zoom: textSize.size }}>
                <thead>
                  <tr>
                    <th className="font-ui sticky top-0 w-px whitespace-nowrap border-b border-zinc-300 bg-white py-2 pl-1 pr-3 text-right text-2xs font-medium tracking-wider text-zinc-400">
                      #
                    </th>
                    <th className="font-ui sticky top-0 whitespace-nowrap border-b border-zinc-300 bg-white py-2 pr-4 text-left text-2xs font-medium uppercase tracking-wider text-zinc-400">
                      <div className="flex items-end gap-1">
                        <span>Source</span>
                        {sort && sort.key.startsWith("_") && (
                          <span className="flex items-center gap-0.5 normal-case tracking-normal text-zinc-500">
                            · {PAPER_FIELDS.find((f) => f.key === sort.key)?.label}
                            {sortMark(sort.key)}
                          </span>
                        )}
                        {rows.length > 0 && (
                          <SheetMenu {...menuProps} fields={paperFields} active={!!sort?.key.startsWith("_") || PAPER_FIELDS.some((f) => filters[f.key])} />
                        )}
                      </div>
                    </th>
                    {building &&
                      PLACEHOLDER_HEADERS.map((length, i) => (
                        <th
                          key={`placeholder-${i}`}
                          aria-hidden
                          className="sticky top-0 min-w-[72px] border-b border-zinc-300 bg-white py-2.5 pr-4 align-bottom"
                        >
                          <div style={{ width: `${length * 0.45}rem` }}>
                            <PulseBlock delay={i * 150} />
                          </div>
                        </th>
                      ))}
                    {columns.map((column, i) => (
                      <th
                        key={column.key}
                        draggable={reorderable}
                        title={reorderable ? "Drag to reorder" : undefined}
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", column.label);
                          dragStart.current = plan?.columns ?? null;
                          setDraggingColumn(column.key);
                        }}
                        onDragOver={(e) => {
                          if (!draggingColumn) return;
                          e.preventDefault();
                          if (draggingColumn !== column.key) moveColumn(draggingColumn, column.key);
                        }}
                        onDrop={(e) => e.preventDefault()}
                        onDragEnd={() => void dropColumn()}
                        className={cn(
                          "sticky top-0 min-w-[72px] max-w-[120px] border-b border-zinc-300 bg-white py-2 pr-4 text-left align-bottom font-normal",
                          reorderable && "cursor-grab active:cursor-grabbing",
                          draggingColumn === column.key && "opacity-50",
                        )}
                      >
                        <div className="font-ui flex items-end gap-1 text-2xs font-medium uppercase leading-tight tracking-wider text-zinc-700">
                          <span className={cn("hyphens-none [overflow-wrap:normal]", live && "row-enter")} style={live ? { animationDelay: `${i * 60}ms` } : undefined}>
                            {column.label}
                          </span>
                          {sortMark(column.key)}
                          {rows.length > 0 && (
                            <SheetMenu {...menuProps} fields={[columnField(column)]} active={sort?.key === column.key || !!filters[column.key]} />
                          )}
                          <button
                            type="button"
                            aria-label={`About ${column.label}`}
                            aria-expanded={panel?.key === column.key}
                            disabled={status !== "done" || readOnly}
                            onClick={() => signInFirst("to edit columns") || setPanel(panel?.key === column.key ? null : { key: column.key })}
                            className={cn("shrink-0 pb-px hover:text-zinc-600", panel?.key === column.key ? "text-zinc-700" : "text-zinc-300")}
                          >
                            <Info className="h-3 w-3" aria-hidden />
                          </button>
                        </div>
                      </th>
                    ))}
                    {pending && !pending.key && (
                      <th className="font-ui sticky top-0 whitespace-nowrap border-b border-zinc-300 bg-white py-2 pr-4 text-left align-bottom text-2xs font-medium uppercase tracking-wider text-zinc-400">
                        {pending.label}
                      </th>
                    )}
                  </tr>
                  {checking && <CheckingLine span={columns.length + 2 + (pending && !pending.key ? 1 : 0)} />}
                </thead>
                <tbody>
                  {(folded ? shown.slice(0, FOLD_ROWS) : shown).map((row, index) => {
                    const rowSelected = selected?.rowKey === row.row_key;
                    return (
                      <Fragment key={row.row_key}>
                        <tr
                          data-row-key={row.row_key}
                          onClick={() => setSelected(rowSelected && !selected?.columnKey ? null : { rowKey: row.row_key, columnKey: null })}
                          className={cn(
                            "row-enter cursor-pointer border-b border-zinc-100 align-top hover:bg-zinc-50",
                            flashing.has(row.row_key) && "row-flash",
                            rowSelected && "border-b-transparent bg-zinc-100 hover:bg-zinc-100",
                          )}
                        >
                          <td className="font-ui w-px whitespace-nowrap py-2.5 pl-1 pr-3 text-right text-2xs tabular-nums text-zinc-400">
                            {index + 1}
                          </td>
                          <td className="max-w-[240px] py-2.5 pr-4" title={row.source.title}>
                            <div className="font-medium text-zinc-900">{paperName(row)}</div>
                            <div className="font-ui mt-0.5 flex flex-wrap items-center gap-x-1.5 text-2xs text-zinc-400">
                              {row.monitor_at && (
                                <span className="rounded-[2px] bg-emerald-100 px-1 font-medium text-emerald-800" title={`Added by an update on ${day(row.monitor_at)}`}>
                                  New
                                </span>
                              )}
                              {designName(row.source.design) && (
                                <span className="rounded-[2px] bg-zinc-100 px-1 font-medium text-zinc-600">{designName(row.source.design)}</span>
                              )}
                              {citationsText(row.source.citations) && (
                                <span className="rounded-[2px] border border-zinc-200 px-1 tabular-nums text-zinc-600">{citationsText(row.source.citations)}</span>
                              )}
                              <JournalLink source={row.source} />
                            </div>
                          </td>
                          {columns.map((column) => {
                            if (pending?.key === column.key) {
                              return (
                                <td key={column.key} className="py-2.5 pr-4">
                                  <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-300" aria-label="Finding values" />
                                </td>
                              );
                            }
                            if (column.value_type === "image") {
                              return (
                                <td key={column.key} data-cell={column.key} className="py-2.5 pr-4">
                                  {row.figures_pending && !row.figures?.[column.key] ? (
                                    <FigureScanning />
                                  ) : (
                                    <FigureCell figure={row.figures?.[column.key]} docId={row.source.doc_id} />
                                  )}
                                </td>
                              );
                            }
                            const cell = row.cells[column.key];
                            const cellSelected = rowSelected && selected?.columnKey === column.key;
                            const isPicked = picked.has(chatCellKey(row.row_key, column.key));
                            return (
                              <td
                                key={column.key}
                                data-cell={column.key}
                                onClick={(e) => {
                                  if (chattable && (e.shiftKey || e.metaKey || e.ctrlKey)) {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    togglePicked(row.row_key, column.key);
                                    return;
                                  }
                                  e.stopPropagation();
                                  setSelected(cellSelected ? null : { rowKey: row.row_key, columnKey: column.key });
                                }}
                                aria-selected={isPicked || undefined}
                                className={cn(
                                  "max-w-[220px] py-2.5 pr-4 text-zinc-700",
                                  !cell && "text-zinc-300",
                                  cell && "hover:underline hover:decoration-dotted",
                                  cell?.written && "italic",
                                  isPicked && "bg-zinc-200/70 outline outline-1 outline-offset-[-2px] outline-zinc-500",
                                  cellSelected && "rounded-[2px] outline outline-2 outline-offset-[-2px] outline-zinc-800",
                                )}
                                title={cell && !cell.derived ? cellOrigin(cell) : undefined}
                              >
                                {cell ? (
                                  <span key={cellText(cell)} className={cn(freshCells.has(chatCellKey(row.row_key, column.key)) && "cell-in")}>
                                    {cellText(cell)}
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                            );
                          })}
                          {pending && !pending.key && (
                            <td className="py-2.5 pr-4">
                              <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-300" aria-label="Finding values" />
                            </td>
                          )}
                        </tr>
                        {rowSelected && selectedRow && (
                          <tr className="border-b border-zinc-200 bg-zinc-100">
                            <td colSpan={columns.length + 2 + (pending && !pending.key ? 1 : 0)} className="px-2 pb-3 pt-1">
                              <VisibleWidth>
                                <EvidencePanel
                                  row={selectedRow}
                                  columns={columns}
                                  focusKey={selected?.columnKey ?? null}
                                  onFocus={(key) => setSelected({ rowKey: selectedRow.row_key, columnKey: key })}
                                  onClose={() => setSelected(null)}
                                  onDeleteRow={editable ? () => void deleteRow(selectedRow.row_key) : signInToEdit ? () => signInFirst("to edit rows") : undefined}
                                  onClearCell={editable ? (key) => void clearCell(selectedRow.row_key, key) : signInToEdit ? () => signInFirst("to edit values") : undefined}
                                  onAddToChat={chattable ? (key) => addToChat([chatCellOf(selectedRow, key)]) : undefined}
                                />
                              </VisibleWidth>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  <SkeletonRows
                    columns={building ? PLACEHOLDER_HEADERS.length : columns.length}
                    count={skeletons}
                    faint={rows.length > 0}
                  />
                </tbody>
              </table>
              </ScrollFade>
              {folded && (
                <button
                  type="button"
                  onClick={() => setUnfolded(true)}
                  className="font-ui flex w-full items-center justify-center gap-1.5 border-b border-zinc-200 py-3 text-xs uppercase tracking-wider text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900"
                >
                  {shown.length - FOLD_ROWS} more
                </button>
              )}
              {!premium && hiddenRows > 0 && (
                <RowLimitNote shown={rows.length} total={rows.length + hiddenRows} tier={quota?.tier} shared={readOnly} />
              )}
              {continuable && !stats.exhausted && rows.length > 0 && (premium || hiddenRows === 0) && (
                <button
                  type="button"
                  onClick={() => more()}
                  className="font-ui mt-0 flex w-full items-center justify-center gap-1.5 border-b border-zinc-200 py-3 text-xs uppercase tracking-wider text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900"
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden /> More rows
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {chatOpen && (datasetId || shareToken) && (
        <FastDatasetChat
          key={datasetId ?? shareToken}
          datasetId={datasetId ?? undefined}
          shareToken={shareToken}
          question={question}
          scale={PAGE_ZOOM}
          onClose={() => setChatOpen(false)}
          onTurnEnd={readOnly ? undefined : reloadRows}
          cells={chatCells}
          onCellsChange={setChatCells}
        />
      )}
    </div>
  );
};

/** The journal or registry of a row's source, linked to the source's own page when one is known. */
function JournalLink({ source }: { source: FastSource }) {
  const link = originalLink(source);
  if (!link) return source.journal ? <FadeText className="max-w-[140px] italic">{source.journal}</FadeText> : null;
  return (
    <a
      href={link.href}
      target="_blank"
      rel="noreferrer"
      onClick={(ev) => ev.stopPropagation()}
      className="inline-flex max-w-[160px] items-center gap-0.5 font-medium text-sky-700 hover:underline"
      title={`View on ${link.label}`}
    >
      <FadeText>{source.journal || link.label}</FadeText>
      <ExternalLink className="h-2.5 w-2.5 shrink-0" aria-hidden />
    </a>
  );
}
